// apps/web/src/components/landing-v2/ProofCard.tsx
import type { ReactNode } from 'react'

type Span = 'narrow' | 'default' | 'wide' | 'full'
type Aspect = 'tall' | 'short'

type Props = {
  /** Grid column span at md+. narrow=4, default=6, wide=8, full=12. */
  span?: Span
  /** Vertical proportion of the visual area. tall=4/5, short=16/8. */
  aspect?: Aspect
  /** Adds the "Cosmic" pill in the corner. */
  cosmicOnly?: boolean
  eyebrow?: string
  title: string
  body?: string
  visual: ReactNode
  className?: string
}

const SPAN_CLASSES: Record<Span, string> = {
  narrow: 'md:col-span-4',
  default: 'md:col-span-6',
  wide: 'md:col-span-8',
  full: 'md:col-span-12',
}

const ASPECT_CLASSES: Record<Aspect, string> = {
  tall: 'aspect-[4/5]',
  short: 'aspect-[16/8]',
}

/** Visual + body container used across all theme proof tiles. The `visual`
 *  slot is rendered into an absolutely-positioned wrapper inside the
 *  aspect-ratioed `.proof-visual` panel, matching the handoff `.proof-card`
 *  pattern in `landing-v2-show.css`. */
export function ProofCard({
  span = 'default',
  aspect = 'short',
  cosmicOnly = false,
  eyebrow,
  title,
  body,
  visual,
  className = '',
}: Props) {
  return (
    <article
      className={`reveal col-span-12 ${SPAN_CLASSES[span]} relative overflow-hidden rounded-xl border border-hairline bg-cosmic-card shadow-card transition-[border-color,transform] duration-300 hover:-translate-y-[3px] hover:border-astral-violet/25 ${className}`}
    >
      {cosmicOnly && (
        <span className="absolute right-3 top-3 z-10 rounded-pill border border-astral-violet/30 bg-astral-violet/20 px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] text-astral-violet-light">
          Cosmic
        </span>
      )}
      <div className={`proof-visual ${ASPECT_CLASSES[aspect]} relative w-full overflow-hidden bg-cosmic-surface`}>
        {visual}
      </div>
      <div className="proof-body px-5 py-5">
        {eyebrow && (
          <p className="text-[10px] uppercase tracking-[0.3em] text-text-muted">{eyebrow}</p>
        )}
        <h3 className="mt-1 font-display text-xl text-text-primary">{title}</h3>
        {body && <p className="mt-2 text-sm text-text-secondary">{body}</p>}
      </div>
    </article>
  )
}
